import React from 'react';
import { createPortal } from 'react-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiAlertTriangle, FiX, FiShield } = FiIcons;

export default function DeleteAccountModal({ account, isDeleting, onCancel, onConfirm }) {
  if (!account) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={isDeleting ? undefined : onCancel}
      />
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-account-title"
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isDeleting}
          className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed"
          aria-label="Close confirmation"
        >
          <SafeIcon icon={FiX} className="text-lg" />
        </button>

        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
          <SafeIcon icon={FiAlertTriangle} className="text-xl text-amber-600" />
        </div>

        <h2 id="delete-account-title" className="text-xl font-bold text-slate-900">
          Archive {account.name}?
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-500">
          This account will remain visible in the Chart of Accounts with an Archived status, but it will no longer be available for new transactions or account selection.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Account Type</p>
            <p className="mt-1 text-sm font-semibold text-slate-700">
              {account.sub_type || account.type || 'Account'}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Archive Effect</p>
            <p className="mt-1 text-sm font-semibold text-slate-700">Prevent future usage</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiShield} className="mt-0.5 shrink-0 text-amber-700" />
            <p className="text-sm leading-6 text-amber-900">
              Associated transactions will <strong>not</strong> be deleted. They remain available for financial reports, reconciliations, and audit history. You can restore this account later if needed.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="rounded-xl bg-amber-600 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-amber-200 transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isDeleting ? 'Archiving...' : 'Archive Account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}